import { Rng } from '@cloudpearl/core';

/**
 * Procedural material maps (tileable), written into typed arrays so they work in any host
 * (browser, worker, expo-gl). Normal maps are tangent-space, RGB = n·0.5+0.5, A = height.
 */

function hash2(x: number, y: number, seed: number): number {
  let h = Math.imul(x | 0, 0x27d4eb2d) ^ Math.imul(y | 0, 0x165667b1) ^ Math.imul(seed | 0, 0x9e3779b1);
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/** Tileable value noise with integer period. */
export function tileNoise(x: number, y: number, period: number, seed: number): number {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const fx = x - xi;
  const fy = y - yi;
  const ux = fx * fx * (3 - 2 * fx);
  const uy = fy * fy * (3 - 2 * fy);
  const m = (v: number): number => ((v % period) + period) % period;
  const a = hash2(m(xi), m(yi), seed);
  const b = hash2(m(xi + 1), m(yi), seed);
  const c = hash2(m(xi), m(yi + 1), seed);
  const d = hash2(m(xi + 1), m(yi + 1), seed);
  return a + (b - a) * ux + (c - a) * uy + (a - b - c + d) * ux * uy;
}

export function tileFbm(x: number, y: number, period: number, seed: number, octaves = 4): number {
  let s = 0;
  let a = 0.5;
  let f = 1;
  let norm = 0;
  for (let o = 0; o < octaves; o++) {
    s += a * tileNoise(x * f, y * f, period * f, seed + o * 13);
    norm += a;
    a *= 0.5;
    f *= 2;
  }
  return s / norm;
}

/** Turn a tileable height field into an RGBA8 normal map (A = height). */
export function heightToNormal(h: Float32Array, size: number, strength: number): Uint8Array {
  const out = new Uint8Array(size * size * 4);
  const at = (x: number, y: number): number => h[(((y + size) % size) * size + ((x + size) % size))]!;
  let mn = Infinity;
  let mx = -Infinity;
  for (const v of h) {
    mn = Math.min(mn, v);
    mx = Math.max(mx, v);
  }
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const dx = (at(x + 1, y) - at(x - 1, y)) * strength;
      const dy = (at(x, y + 1) - at(x, y - 1)) * strength;
      let nx = -dx;
      let ny = -dy;
      let nz = 1;
      const l = Math.hypot(nx, ny, nz);
      nx /= l;
      ny /= l;
      nz /= l;
      const i = (y * size + x) * 4;
      out[i] = Math.round((nx * 0.5 + 0.5) * 255);
      out[i + 1] = Math.round((ny * 0.5 + 0.5) * 255);
      out[i + 2] = Math.round((nz * 0.5 + 0.5) * 255);
      out[i + 3] = Math.round(((at(x, y) - mn) / (mx - mn || 1)) * 255);
    }
  }
  return out;
}

/**
 * Wind ripple normal map (Journey / Zucconi style). `steep` makes tighter, straighter ripples for
 * slip faces. Crests run perpendicular to u; +u is downwind.
 */
export function paintSandRipples(size: number, steep: boolean, seed: number): Uint8Array {
  // Built the way wind builds them: long parallel crests, gently sinuous along their length;
  // each an asymmetric ridge (long gentle windward slope, short steep lee), with occasional
  // defects where one crest ends and two merge into a Y.
  const h = new Float32Array(size * size);
  const waves = steep ? 26 : 16;
  const crest = steep ? 0.8 : 0.74;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const u = x / size;
      const v = y / size;
      // Sinuosity: slow wander along the crest (v), barely any across it.
      const wander = (tileFbm(u * 1 + 7.3, v * 3, 3, seed, 3) - 0.5) * (steep ? 0.9 : 1.6);
      // Defects: the phase jumps half a cycle across a meandering line → crests fork and merge.
      const defect = tileFbm(u * 2 + 1.1, v * 2 - 4.2, 2, seed + 7, 3) - 0.5;
      const jump = 0.5 * (0.5 + 0.5 * Math.tanh(defect * 18));
      const phase = u * waves + wander + jump;
      const f = phase - Math.floor(phase);
      // Asymmetric profile, softly rounded at crest and trough.
      let r = f < crest ? f / crest : 1 - (f - crest) / (1 - crest);
      r = r * r * (3 - 2 * r);
      const amp = 0.85 + 0.15 * tileFbm(u * 3 + 3, v * 3 - 2, 3, seed + 9, 2);
      const grain = (tileNoise(u * 256, v * 256, 256, seed + 21) - 0.5) * 0.03;
      h[y * size + x] = r * amp + grain;
    }
  }
  return heightToNormal(h, size, steep ? 2.6 : 2.2);
}

/** Fine sand grain: per-texel random micro-normals, lightly blurred. */
export function paintSandGrain(size: number, seed: number): Uint8Array {
  const h = new Float32Array(size * size);
  for (let i = 0; i < h.length; i++) h[i] = hash2(i % size, Math.floor(i / size), seed);
  const b = new Float32Array(size * size);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      let s = 0;
      for (let k = -1; k <= 1; k++) for (let l = -1; l <= 1; l++) s += h[(((y + k + size) % size) * size + ((x + l + size) % size))]! * (k === 0 && l === 0 ? 4 : 1);
      b[y * size + x] = s / 12;
    }
  }
  return heightToNormal(b, size, 6);
}

/** Random unit vectors for glitter (RGB = direction, A = threshold random). */
export function paintGlitter(size: number, seed: number): Uint8Array {
  const out = new Uint8Array(size * size * 4);
  const rng = new Rng(seed, 333);
  for (let i = 0; i < size * size; i++) {
    let x = rng.float() * 2 - 1;
    let y = rng.float() * 2 - 1;
    let z = rng.float() * 2 - 1 + 0.6;
    const l = Math.hypot(x, y, z) || 1;
    x /= l;
    y /= l;
    z /= l;
    out[i * 4] = Math.round((x * 0.5 + 0.5) * 255);
    out[i * 4 + 1] = Math.round((y * 0.5 + 0.5) * 255);
    out[i * 4 + 2] = Math.round((z * 0.5 + 0.5) * 255);
    out[i * 4 + 3] = Math.round(rng.float() * 255);
  }
  return out;
}

/** Bark: deep vertical fissures with plates between them. A = height (used for albedo too). */
export function paintBarkNormal(size: number, seed: number): Uint8Array {
  const h = new Float32Array(size * size);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const u = x / size;
      const v = y / size;
      const warp = (tileFbm(u * 3, v * 2, 3, seed, 3) - 0.5) * 0.5;
      const plates = tileFbm(u * 10 + warp * 3, v * 2.5, 10, seed + 4, 3);
      const fissure = 1 - Math.pow(Math.abs(Math.sin((u * 7 + warp) * Math.PI)), 0.35);
      const fine = tileNoise(u * 64, v * 16, 64, seed + 8) * 0.15;
      h[y * size + x] = plates * 0.6 - fissure * 0.55 + fine;
    }
  }
  return heightToNormal(h, size, 3.2);
}

/** Rock: cracks (cellular) over grain. A = height. */
export function paintRockNormal(size: number, seed: number): Uint8Array {
  const h = new Float32Array(size * size);
  const cells = 6;
  const pts: [number, number][] = [];
  const rng = new Rng(seed, 444);
  for (let j = 0; j < cells; j++) for (let i = 0; i < cells; i++) pts.push([(i + rng.float()) / cells, (j + rng.float()) / cells]);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      // Domain-warp the cells so cracks wander instead of tiling into cobbles.
      const u = x / size + (tileFbm((x / size) * 4, (y / size) * 4, 4, seed + 7, 3) - 0.5) * 0.22;
      const v = y / size + (tileFbm((x / size) * 4 + 3.1, (y / size) * 4 + 1.7, 4, seed + 9, 3) - 0.5) * 0.22;
      let d1 = 9;
      let d2 = 9;
      for (const [px, py] of pts) {
        for (let oy = -1; oy <= 1; oy++) {
          for (let ox = -1; ox <= 1; ox++) {
            const dx = u - (px + ox);
            const dy = v - (py + oy);
            const d = dx * dx + dy * dy;
            if (d < d1) {
              d2 = d1;
              d1 = d;
            } else if (d < d2) d2 = d;
          }
        }
      }
      const crack = Math.min(1, (Math.sqrt(d2) - Math.sqrt(d1)) * 10);
      const grain = tileFbm((x / size) * 16, (y / size) * 16, 16, seed + 2, 5);
      const lumps = tileFbm((x / size) * 5, (y / size) * 5, 5, seed + 4, 3);
      h[y * size + x] = crack * 0.3 + grain * 0.55 + lumps * 0.6;
    }
  }
  return heightToNormal(h, size, 2.4);
}

/** Meadow soil / ground detail. */
export function paintGroundNormal(size: number, seed: number): Uint8Array {
  const h = new Float32Array(size * size);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const u = x / size;
      const v = y / size;
      h[y * size + x] = tileFbm(u * 8, v * 8, 8, seed, 5) + tileNoise(u * 64, v * 64, 64, seed + 3) * 0.2;
    }
  }
  return heightToNormal(h, size, 3);
}

/**
 * Leaf card v2: a dense cluster of small leaves (each with a vein and darker edge) instead of a few
 * big strokes — reads as real foliage once thousands of cards overlap.
 * R = shade value, G = per-leaf hue random, B = vein/edge detail, A = coverage.
 */
export function paintLeafCluster(size: number, seed: number): Uint8Array {
  const r = new Float32Array(size * size);
  const g = new Float32Array(size * size);
  const b = new Float32Array(size * size);
  const a = new Float32Array(size * size);
  const rng = new Rng(seed, 99);
  const leaves = 11;
  const c = size / 2;
  for (let k = 0; k < leaves; k++) {
    const ang = rng.float() * Math.PI * 2;
    const rad = Math.sqrt(rng.float()) * size * 0.3;
    const lx = c + Math.cos(ang) * rad;
    const ly = c + Math.sin(ang) * rad;
    const len = size * rng.range(0.14, 0.2);
    const wid = len * rng.range(0.36, 0.48);
    const dir = ang + Math.PI / 2 + rng.range(-0.9, 0.9);
    const cd = Math.cos(dir);
    const sd = Math.sin(dir);
    const value = rng.range(0.35, 1);
    const hue = rng.float();
    const bend = rng.range(-0.25, 0.25);
    const ext = Math.ceil(len + 2);
    for (let y = Math.max(0, Math.floor(ly - ext)); y < Math.min(size, ly + ext); y++) {
      for (let x = Math.max(0, Math.floor(lx - ext)); x < Math.min(size, lx + ext); x++) {
        const dx = x + 0.5 - lx;
        const dy = y + 0.5 - ly;
        const u = (dx * cd + dy * sd) / len; // along leaf, -1..1
        let vv = (-dx * sd + dy * cd) / wid; // across
        vv -= bend * (1 - u * u);
        if (u < -1 || u > 1) continue;
        const half = Math.pow(Math.max(0, 1 - u * u), 0.75) * (u < 0 ? 0.92 : 1);
        const e = Math.abs(vv) / Math.max(half, 1e-3);
        if (e >= 1) continue;
        const i = y * size + x;
        const cov = Math.min(1, (1 - e) * 6);
        // Painter's order: later leaves cover earlier ones.
        const vein = Math.max(0, 1 - Math.abs(vv) * 18) * (1 - Math.abs(u)) * 0.6;
        const side = vv > 0 ? 0.08 : -0.06;
        const edge = Math.pow(e, 6);
        r[i] = r[i]! * (1 - cov) + Math.min(1, value + side - edge * 0.3 + (u + 1) * 0.06) * cov;
        g[i] = g[i]! * (1 - cov) + hue * cov;
        b[i] = b[i]! * (1 - cov) + Math.min(1, vein + edge * 0.5) * cov;
        a[i] = Math.max(a[i]!, cov);
      }
    }
  }
  const out = new Uint8Array(size * size * 4);
  for (let i = 0; i < size * size; i++) {
    const x = i % size;
    const y = Math.floor(i / size);
    const border = Math.min(x, y, size - 1 - x, size - 1 - y) < 2 ? 0 : 1;
    out[i * 4] = Math.round(r[i]! * 255);
    out[i * 4 + 1] = Math.round(g[i]! * 255);
    out[i * 4 + 2] = Math.round(b[i]! * 255);
    out[i * 4 + 3] = Math.round(a[i]! * border * 255);
  }
  return out;
}

function hash3(x: number, y: number, z: number, seed: number): number {
  let h = Math.imul(x | 0, 0x27d4eb2d) ^ Math.imul(y | 0, 0x165667b1) ^ Math.imul(z | 0, 0x1b873593) ^ Math.imul(seed | 0, 0x9e3779b1);
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

function noise3d(x: number, y: number, z: number, seed: number): number {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const zi = Math.floor(z);
  const fx = x - xi;
  const fy = y - yi;
  const fz = z - zi;
  const ux = fx * fx * (3 - 2 * fx);
  const uy = fy * fy * (3 - 2 * fy);
  const uz = fz * fz * (3 - 2 * fz);
  const l = (a: number, b: number, t: number): number => a + (b - a) * t;
  const c = (i: number, j: number, k: number): number => hash3(xi + i, yi + j, zi + k, seed);
  return l(l(l(c(0, 0, 0), c(1, 0, 0), ux), l(c(0, 1, 0), c(1, 1, 0), ux), uy), l(l(c(0, 0, 1), c(1, 0, 1), ux), l(c(0, 1, 1), c(1, 1, 1), ux), uy), uz);
}

function fbm3d(x: number, y: number, z: number, seed: number, octaves: number): number {
  let s = 0;
  let a = 0.5;
  let norm = 0;
  for (let o = 0; o < octaves; o++) {
    s += a * noise3d(x, y, z, seed + o * 31);
    norm += a;
    x = x * 2.03 + 1.7;
    y = y * 2.03 - 3.1;
    z = z * 2.03 + 5.3;
    a *= 0.5;
  }
  return s / norm;
}

/**
 * Imperfection maps for the pearl, in the equirectangular UV layout of three's SphereGeometry.
 * Heights come from 3D noise on the sphere (no seams, no pole pinching): a faint out-of-round
 * swell, a fine "orange peel" and a scatter of tiny dimples. Roughness (G channel) gets soft
 * smudges so reflections break up subtly across the surface.
 */
export function paintPearlMaps(width: number, height: number, seed: number): { normal: Uint8Array; roughness: Uint8Array } {
  const rng = new Rng(seed, 707);
  const dimples: { x: number; y: number; z: number; w: number; d: number }[] = [];
  for (let i = 0; i < 46; i++) {
    let x = rng.float() * 2 - 1;
    let y = rng.float() * 2 - 1;
    let z = rng.float() * 2 - 1;
    const l = Math.hypot(x, y, z) || 1;
    x /= l;
    y /= l;
    z /= l;
    const ang = rng.range(0.025, 0.075);
    dimples.push({ x, y, z, w: (ang * ang) / 2, d: rng.range(0.4, 1.0) });
  }
  const H = new Float32Array(width * height);
  const R = new Float32Array(width * height);
  for (let j = 0; j < height; j++) {
    const v = (j + 0.5) / height;
    const theta = (1 - v) * Math.PI;
    const st = Math.sin(theta);
    const ct = Math.cos(theta);
    for (let i = 0; i < width; i++) {
      const phi = ((i + 0.5) / width) * Math.PI * 2;
      const px = -Math.cos(phi) * st;
      const py = ct;
      const pz = Math.sin(phi) * st;
      let h = (fbm3d(px * 2.2 + 3, py * 2.2, pz * 2.2, seed, 3) - 0.5) * 1.2;
      h += (fbm3d(px * 16, py * 16, pz * 16, seed + 9, 3) - 0.5) * 0.22;
      let dim = 0;
      for (const d of dimples) {
        const k = 1 - (px * d.x + py * d.y + pz * d.z);
        if (k < d.w * 6) dim += d.d * Math.exp(-k / d.w);
      }
      h -= dim * 0.35;
      H[j * width + i] = h;
      const smudge = fbm3d(px * 3.4 + 7, py * 3.4, pz * 3.4, seed + 21, 4);
      R[j * width + i] = 0.06 + Math.max(0, smudge - 0.48) * 0.55 + dim * 0.05;
    }
  }
  const normal = new Uint8Array(width * height * 4);
  const roughness = new Uint8Array(width * height * 4);
  const at = (i: number, j: number): number => H[Math.min(height - 1, Math.max(0, j)) * width + ((i + width) % width)]!;
  for (let j = 0; j < height; j++) {
    const theta = (1 - (j + 0.5) / height) * Math.PI;
    const st = Math.max(0.08, Math.sin(theta));
    for (let i = 0; i < width; i++) {
      // Height gradient per unit arc length (radius 1) along +u (east) and +v (toward +y).
      const du = (at(i + 1, j) - at(i - 1, j)) / (2 * ((2 * Math.PI) / width) * st);
      const dv = (at(i, j + 1) - at(i, j - 1)) / (2 * (Math.PI / height));
      const s = 0.012;
      let nx = -du * s;
      let ny = -dv * s;
      let nz = 1;
      const l = Math.hypot(nx, ny, nz);
      nx /= l;
      ny /= l;
      nz /= l;
      const o = (j * width + i) * 4;
      normal[o] = Math.round((nx * 0.5 + 0.5) * 255);
      normal[o + 1] = Math.round((ny * 0.5 + 0.5) * 255);
      normal[o + 2] = Math.round((nz * 0.5 + 0.5) * 255);
      normal[o + 3] = 255;
      const r = Math.min(1, R[j * width + i]!);
      roughness[o] = roughness[o + 1] = roughness[o + 2] = Math.round(r * 255);
      roughness[o + 3] = 255;
    }
  }
  return { normal, roughness };
}
